export type CartItem = {
  id: number;
  productId: number;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  name: string;
  slug: string;
  category: string;
  description: string;
  testedWith: string | null;
  image: string | null;
  inStock: boolean;
  availableQuantity: number;
};

export type CartResponse = {
  items: CartItem[];
  summary: {
    subtotal: number;
    discount: number;
    total: number;
    quantity: number;
    coupon?: {
      id: number;
      code: string;
      description: string;
      discount: number;
      type: "FIXED" | "PERCENTAGE";
    } | null;
  };
};

const cartUpdatedEvent = "nv-cart-updated";

export function emitCartUpdated(cart: CartResponse) {
  window.dispatchEvent(
    new CustomEvent<CartResponse>(cartUpdatedEvent, {
      detail: cart
    })
  );
}

export function subscribeToCartUpdates(listener: (cart: CartResponse) => void) {
  function handle(event: Event) {
    const customEvent = event as CustomEvent<CartResponse>;
    listener(customEvent.detail);
  }

  window.addEventListener(cartUpdatedEvent, handle);
  return () => {
    window.removeEventListener(cartUpdatedEvent, handle);
  };
}

export async function fetchCart() {
  const response = await fetch("/api/cart", {
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  if (!response.ok) {
    throw new Error(`Cart request failed: ${response.status}`);
  }

  return (await response.json()) as CartResponse;
}

export async function addCartItem(input: { productId: number; quantity: number }) {
  const response = await fetch("/api/cart/items", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to add item to cart");
  }

  emitCartUpdated(data.cart as CartResponse);
  return data.cart as CartResponse;
}

export async function updateCartItem(itemId: number, quantity: number) {
  const response = await fetch(`/api/cart/items/${itemId}`, {
    method: "PATCH",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify({ quantity })
  });

  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to update cart");
  }

  emitCartUpdated(data.cart as CartResponse);
  return data.cart as CartResponse;
}

export async function deleteCartItem(itemId: number) {
  const response = await fetch(`/api/cart/items/${itemId}`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      Accept: "application/json"
    }
  });

  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to remove item");
  }

  emitCartUpdated(data.cart as CartResponse);
  return data.cart as CartResponse;
}

export async function submitCheckout(input: {
  email: string;
  phone: string;
  name: string;
  deliveryLocation: string;
  deliveryNote?: string;
  couponCode?: string;
}) {
  const response = await fetch("/api/cart/checkout", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to submit checkout");
  }

  return data as {
    status: "success";
    orderId: number;
    total: number;
    message: string;
  };
}

export async function previewCoupon(input: { couponCode: string }) {
  const response = await fetch("/api/cart/coupon", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json"
    },
    body: JSON.stringify(input)
  });

  const data = await response.json();
  if (!response.ok || data.status !== "success") {
    throw new Error(data.message || "Unable to apply coupon");
  }

  return data.cart as CartResponse;
}
